import { usernameFormatCode } from "@pax/api-contract/common";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ApiProblem } from "@/components/ApiProblem";
import { NotFound } from "@/components/RouteError";
import { useSession } from "@/features/auth/queries";
import { ProfileScreen, ProfileSkeleton } from "@/features/profiles/components/ProfileScreen";
import { profileByUsernameQuery, profileQuery } from "@/features/profiles/queries";
import { isNotFound, requestErrorMessage } from "@/lib/api";

/**
 * A profile at its username, such as `/@Grace_Hopper`. A username nobody holds now, including one
 * its owner renamed away from, is Not found.
 */
export const Route = createFileRoute("/_authenticated/@{$username}")({
  // Not awaited, so the page opens on its skeleton rather than waiting on the request.
  loader: ({ context: { queryClient }, params: { username } }) => {
    if (!usernameFormatCode(username))
      void queryClient.prefetchQuery(profileByUsernameQuery(username));
  },
  head: ({ params }) => ({ meta: [{ title: `@${params.username} · Pax.co` }] }),
  component: UsernamePage,
});

function UsernamePage() {
  const { username } = Route.useParams();
  const valid = usernameFormatCode(username) === null;
  const { data: session } = useSession();
  const lookup = useQuery({ ...profileByUsernameQuery(username), enabled: valid });
  const profileId = lookup.data?.profile.profileId;
  // Read through the profile's own query once its id is known, so edits made on this page land.
  const profile = useQuery({
    ...profileQuery(profileId ?? ""),
    enabled: profileId !== undefined,
    initialData: lookup.data,
  });

  if (!valid || isNotFound(lookup.error)) return <NotFound />;
  if (lookup.error) return <ApiProblem message={requestErrorMessage(lookup.error)} />;
  if (!profile.data) return <ProfileSkeleton />;

  const viewerId = session && !session.user.isAnonymous ? session.user.id : null;
  return <ProfileScreen key={profile.data.profileId} profile={profile.data} viewerId={viewerId} />;
}
